import React, { type ReactNode } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

const messages: Record<'errorTitle' | 'reloadLink', MessageDescriptor> = defineMessages({
	errorTitle: {
		id: 'ai-mate.conversation-assistant.session-right-panel.error-boundary-title-non-final',
		defaultMessage: 'Something went wrong',
		description:
			'Title shown in place of the session context panel when it fails to load. The conversation itself is unaffected.',
	},
	reloadLink: {
		id: 'ai-mate.conversation-assistant.session-right-panel.error-boundary-reload-link-non-final',
		defaultMessage: 'Reload to try again',
		description:
			'Link shown below the error title in the session context panel, which reloads the page to recover from the failure.',
	},
});

const styles = cssMap({
	errorState: {
		height: '100%',
		paddingBlock: token('space.400'),
		paddingInline: token('space.200'),
	},
	reloadButton: {
		backgroundColor: token('color.background.neutral.subtle'),
		border: 'none',
		display: 'inline',
		color: token('color.link'),
		cursor: 'pointer',
		paddingBlock: token('space.0'),
		paddingInline: token('space.0'),
	},
});

/**
 * Text-only counterpart to ChatGenericError implemented for the context panel.
 * This is to avoid an unnecessary bundle size increase with ChatGenericError's illustration
 */
const SessionRightPanelErrorState = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Flex
			direction="column"
			alignItems="center"
			justifyContent="center"
			xcss={styles.errorState}
			testId="conversation-assistant-session-context-panel-error"
		>
			<Stack space="space.150" alignInline="center">
				<Heading size="medium">{formatMessage(messages.errorTitle)}</Heading>
				<Text as="p">
					<Pressable
						xcss={styles.reloadButton}
						onClick={() => {
							window.location.reload();
						}}
					>
						{formatMessage(messages.reloadLink)}
					</Pressable>
				</Text>
			</Stack>
		</Flex>
	);
};

export type SessionRightPanelErrorBoundaryProps = {
	children: ReactNode;
};

/**
 * Captures unhandled errors occurred within session context panel and report them using operational analytics events
 */
export const SessionRightPanelErrorBoundary = ({
	children,
}: SessionRightPanelErrorBoundaryProps): JSX.Element => {
	const { sendAnalyticsEvent } = useAnalytics();

	return (
		<ErrorBoundary
			fallback={<SessionRightPanelErrorState />}
			onError={(error) => {
				logException({
					exception: error,
					name: 'sessionContextPanel',
					tags: { teamName: 'deliveroo' },
				});

				sendAnalyticsEvent({
					eventType: 'operational',
					action: 'failed',
					actionSubject: 'sessionContextPanel',
					attributes: {
						errorMessage: error.message,
					},
				});
			}}
		>
			{children}
		</ErrorBoundary>
	);
};
